import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, Product, KeywordWithIntent, IntentCategoryConfig, ViewType } from '../types';
import FileUpload from './FileUpload';
import ViewTypeSelectionModal from './ViewTypeSelectionModal';

interface NewProjectModalProps {
  onClose: () => void;
  onProjectCreated: (project: Project) => void;
}

interface ProcessedKeywordData {
  productName?: string;
  productCategory: string;
  keywords: KeywordWithIntent[];
  ingredients?: string;
  legalClaims?: string;
  copy?: string;
  brand?: string;
  subBrand?: string;
}

const NewProjectModal: React.FC<NewProjectModalProps> = ({ onClose, onProjectCreated }) => {
  const [projectName, setProjectName] = useState('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showViewTypeSelection, setShowViewTypeSelection] = useState(false);
  const [availableViewTypes, setAvailableViewTypes] = useState<ViewType[]>([]);
  const [pendingData, setPendingData] = useState<{
    data: ProcessedKeywordData[];
    intentCategories: string[];
    intentCategoryConfig: Record<string, IntentCategoryConfig>;
    fileInfo: { name: string; size: number; data: string };
  } | null>(null);
  const navigate = useNavigate();

  const handleImportData = (
    importedData: ProcessedKeywordData[], 
    intentCategories: string[], 
    intentCategoryConfig: Record<string, IntentCategoryConfig>,
    availableViewTypes: ViewType[],
    fileInfo: { name: string; size: number; data: string }
  ) => {
    if (!projectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    // Store data and show view type selection
    setPendingData({ data: importedData, intentCategories, intentCategoryConfig, fileInfo });
    setAvailableViewTypes(availableViewTypes);
    setShowViewTypeSelection(true);
  };

  const handleViewTypeSelected = (viewType: ViewType) => {
    if (!pendingData) return;

    const { data: importedData, intentCategories, intentCategoryConfig, fileInfo } = pendingData;

    // Create products from imported data
    const products: Product[] = importedData.map((data, index) => ({
      id: `product-${Date.now()}-${index}`,
      name: data.productName || data.brand || data.subBrand || 'Unnamed',
      description: '', // No description needed
      category: data.productCategory,
      keywords: data.keywords,
      selectedKeywords: [],
      status: 'pending' as const,
      ingredients: data.ingredients,
      legalClaims: data.legalClaims,
      copy: data.copy,
      brand: data.brand,
      subBrand: data.subBrand
    }));

    // Create new project with imported data
    const newProject: Project = {
      id: `project-${Date.now()}`,
      name: projectName.trim(),
      description: `Project with ${products.length} items imported from Excel`,
      brand: {
        id: 'default-brand',
        name: 'Default Brand',
        color: '#6366f1' // Default brand color
      },
      products: products,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
      totalProducts: products.length,
      completedProducts: 0,
      intentCategories: intentCategories.length > 0 ? intentCategories : undefined,
      intentCategoryConfig: Object.keys(intentCategoryConfig).length > 0 ? intentCategoryConfig : undefined,
      viewType: viewType,
      originalFile: {
        name: fileInfo.name,
        size: fileInfo.size,
        uploadedAt: new Date(),
        data: fileInfo.data
      }
    };

    onProjectCreated(newProject);
    navigate(`/project/${newProject.id}`);
    onClose();
  };

  const handleViewTypeCancel = () => {
    setShowViewTypeSelection(false);
    setPendingData(null);
  };

  const handleFileUploadClose = () => {
    setShowFileUpload(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary-900">Create New Project</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excel File <span className="text-red-500">*</span>
            </label>
            <button
              onClick={() => setShowFileUpload(true)}
              disabled={!projectName.trim()}
              className={`w-full px-4 py-3 rounded-lg transition-colors ${
                !projectName.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-accent-500 text-white hover:bg-accent-600'
              }`}
            >
              Select Excel File
            </button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            Both project name and Excel file are required to create a new project
          </div>
        </div>

        {/* File Upload Modal */}
        {showFileUpload && (
          <FileUpload
            onDataProcessed={handleImportData}
            onClose={handleFileUploadClose}
          />
        )}

        {/* View Type Selection Modal */}
        {showViewTypeSelection && (
          <ViewTypeSelectionModal
            availableViewTypes={availableViewTypes}
            onViewTypeSelected={handleViewTypeSelected}
            onCancel={handleViewTypeCancel}
          />
        )}
      </div>
    </div>
  );
};

export default NewProjectModal;
